"use client";

import { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import { PostHogProvider } from "@/components/analytics/posthog-provider";
import { useConsentStore } from "@/lib/analytics/consent";

// Lazy load Vercel Analytics during browser idle time
const Analytics = dynamic(
	() => import("@vercel/analytics/next").then((mod) => ({ default: mod.Analytics })),
	{ ssr: false }
);

export function AnalyticsWrapper({ children }: { children?: React.ReactNode }) {
	const [shouldLoadAnalytics, setShouldLoadAnalytics] = useState(false);
	const [shouldLoadPostHog, setShouldLoadPostHog] = useState(false);
	const [isClient, setIsClient] = useState(false);

	// Ensure we're on the client before accessing zustand store
	useEffect(() => {
		setIsClient(true);
	}, []);

	// Always call hooks, but conditionally use their values
	const storeCanTrack = useConsentStore((state) => state.canTrack);
	const storeCanTrackPostHog = useConsentStore(
		(state) => state.canTrackPostHog,
	);

	useEffect(() => {
		if (isClient) {
			setShouldLoadAnalytics(storeCanTrack());
			setShouldLoadPostHog(storeCanTrackPostHog());
		}
	}, [isClient, storeCanTrack, storeCanTrackPostHog]);

	const content = children || null;

	if (!shouldLoadAnalytics && !shouldLoadPostHog) {
		return <>{content}</>;
	}

	return (
		<>
			{shouldLoadPostHog ? (
				<PostHogProvider>{content}</PostHogProvider>
			) : (
				content
			)}
			{shouldLoadAnalytics && <Analytics />}
		</>
	);
}
